import { useMemo } from 'react';
import type { Dictamen } from '../views/Dictamenes/types';

export interface EstadisticasDictamenes {
  total: number;
  porTipo: Record<string, number>;
  porPago: Record<string, number>;
  porOperador: Record<string, number>;
  ingresoTotal: number;
  ingresosPagados: number;
  porcentajePagado: number;
  porMes: Array<{ mes: string; count: number; ingresos: number }>;
  promedioMensual: number;
  dictamenMasReciente: Dictamen | null;
}

export function useEstadisticasDictamenes(dictamenes: Dictamen[]): EstadisticasDictamenes {
  return useMemo(() => {
    const total = dictamenes.length;
    
    // Conteo por tipo
    const porTipo = dictamenes.reduce((acc, d) => {
      const tipo = d.tipo || 'Sin tipo';
      acc[tipo] = (acc[tipo] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Conteo por estado de pago
    const porPago = dictamenes.reduce((acc, d) => {
      const estado = d.estatusPago || 'No pagado';
      acc[estado] = (acc[estado] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Conteo por operador
    const porOperador = dictamenes.reduce((acc, d) => {
      const operador = d.operador || 'Sin asignar';
      acc[operador] = (acc[operador] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Ingresos
    const ingresoTotal = dictamenes.reduce((sum, d) => sum + (Number(d.montoPago) || 0), 0);
    const ingresosPagados = dictamenes
      .filter(d => d.estatusPago === 'Pagado')
      .reduce((sum, d) => sum + (Number(d.montoPago) || 0), 0);

    // Dictámenes por mes (últimos 6 meses)
    const ahora = new Date();
    const porMes = Array.from({ length: 6 }, (_, i) => {
      const fecha = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
      const mes = fecha.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
      
      const dictamenesMes = dictamenes.filter(d => {
        if (!d.fechaSolicitud) return false;
        const fechaDict = new Date(d.fechaSolicitud);
        return fechaDict.getMonth() === fecha.getMonth() && 
               fechaDict.getFullYear() === fecha.getFullYear();
      });
      
      const count = dictamenesMes.length;
      const ingresos = dictamenesMes.reduce((sum, d) => sum + (Number(d.montoPago) || 0), 0);
      
      return { mes, count, ingresos };
    }).reverse();

    // Promedio mensual
    const promedioMensual = porMes.length > 0 
      ? porMes.reduce((sum, m) => sum + m.count, 0) / porMes.length 
      : 0;

    // Dictamen más reciente
    const dictamenMasReciente = dictamenes
      .filter(d => d.fechaSolicitud)
      .sort((a, b) => new Date(b.fechaSolicitud!).getTime() - new Date(a.fechaSolicitud!).getTime())[0] || null;

    return {
      total,
      porTipo,
      porPago,
      porOperador,
      ingresoTotal,
      ingresosPagados,
      porcentajePagado: total > 0 ? ((porPago['Pagado'] || 0) / total) * 100 : 0,
      porMes,
      promedioMensual,
      dictamenMasReciente
    };
  }, [dictamenes]);
}